import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Row, Select, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { CardDropsRow, GroupAnalyticsParams } from '@/types';
import { adminApi } from '@services/api';
import { reportLoadErrorMessage } from '@/utils/apiError';
import { DIVISION_NAMES } from '@/constants/divisionConstants';

const { Text } = Typography;

type Outcome = CardDropsRow['matchOutcome'];

const MODE_LABELS: Record<string, string> = {
  quick_play: 'Quick Play',
  custom_match: 'Custom Match',
  blitz_showdown: 'Blitz Showdown',
};

const modeLabel = (matchType: string) =>
  MODE_LABELS[matchType] ??
  matchType
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const divisionLabel = (tier: number) => DIVISION_NAMES[tier] ?? `Division ${tier}`;

const OUTCOME_ORDER: Record<Outcome, number> = { win: 0, draw: 1, loss: 2 };

const OUTCOME_TAG: Record<Outcome, { color?: string; label: string }> = {
  win: { color: 'green', label: 'Win' },
  draw: { label: 'Draw' },
  loss: { color: 'red', label: 'Loss' },
};

const ALL_MODES = '__all__';

const fmt = (n: number) => n.toLocaleString('en-IN');

/** null when there is no denominator — an unknown rate, never 0. */
const ratio = (n: number, d: number): number | null => (d > 0 ? n / d : null);

const fmtPct = (r: number | null) => (r === null ? '—' : `${(r * 100).toFixed(1)}%`);

const fmtPerMatch = (r: number | null) =>
  r === null
    ? '—'
    : r.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface DropRow extends CardDropsRow {
  key: string;
  mode: string;
  division: string;
  /** Matches whose drops are known: completed minus those without a record. */
  recorded: number;
  dropRate: number | null;
  cardsPerMatch: number | null;
}

const COLUMNS: ColumnsType<DropRow> = [
  { title: 'Mode', dataIndex: 'mode', key: 'mode' },
  { title: 'Division', dataIndex: 'division', key: 'division' },
  {
    title: 'Result',
    dataIndex: 'matchOutcome',
    key: 'matchOutcome',
    render: (o: Outcome) => <Tag color={OUTCOME_TAG[o].color}>{OUTCOME_TAG[o].label}</Tag>,
  },
  {
    title: 'Matches',
    dataIndex: 'matches',
    key: 'matches',
    align: 'right',
    sorter: (a, b) => a.matches - b.matches,
    render: (n: number) => fmt(n),
  },
  {
    title: 'With drops (%)',
    key: 'matchesWithDrops',
    align: 'right',
    sorter: (a, b) => (a.dropRate ?? -1) - (b.dropRate ?? -1),
    render: (_: unknown, r) => `${fmt(r.matchesWithDrops)} (${fmtPct(r.dropRate)})`,
  },
  {
    title: 'Cards',
    dataIndex: 'cards',
    key: 'cards',
    align: 'right',
    sorter: (a, b) => a.cards - b.cards,
    render: (n: number) => fmt(n),
  },
  {
    title: 'Cards/match',
    dataIndex: 'cardsPerMatch',
    key: 'cardsPerMatch',
    align: 'right',
    sorter: (a, b) => (a.cardsPerMatch ?? -1) - (b.cardsPerMatch ?? -1),
    render: (r: number | null) => fmtPerMatch(r),
  },
  {
    title: 'Unique players',
    dataIndex: 'uniqueUsers',
    key: 'uniqueUsers',
    align: 'right',
    sorter: (a, b) => a.uniqueUsers - b.uniqueUsers,
    render: (n: number) => fmt(n),
  },
  {
    title: 'No record',
    dataIndex: 'matchesWithoutRecord',
    key: 'matchesWithoutRecord',
    align: 'right',
    sorter: (a, b) => a.matchesWithoutRecord - b.matchesWithoutRecord,
    render: (n: number) =>
      n > 0 ? <Text type="warning">{fmt(n)}</Text> : <Text type="secondary">0</Text>,
  },
];

/**
 * Cards granted by completed matches, by mode, division and result.
 *
 * Rates and cards per match are taken over matches with a recorded result
 * only. A match with no record has unknown drops; folding it into the
 * denominator would count it as a zero-drop match and drag the rate down.
 */
export const CardDropsCard: React.FC<{ params: GroupAnalyticsParams }> = ({ params }) => {
  const [rows, setRows] = useState<CardDropsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<string>(ALL_MODES);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getCardDrops(params);
      setRows(res.data ?? []);
      setError(null);
    } catch (e) {
      setError(reportLoadErrorMessage(e, 'Could not load card drops'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    void load();
  }, [load]);

  const modes = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.matchType))).sort((a, b) =>
        modeLabel(a).localeCompare(modeLabel(b))
      ),
    [rows]
  );

  // A new date range can drop the selected mode; fall back to all modes
  // rather than showing an empty table.
  const activeMode = useMemo(
    () => (mode !== ALL_MODES && modes.includes(mode) ? mode : ALL_MODES),
    [mode, modes]
  );

  const modeOptions = useMemo(
    () => [
      { value: ALL_MODES, label: 'All modes' },
      ...modes.map((m) => ({ value: m, label: modeLabel(m) })),
    ],
    [modes]
  );

  const visible = useMemo<DropRow[]>(
    () =>
      rows
        .filter((r) => activeMode === ALL_MODES || r.matchType === activeMode)
        .map((r) => {
          const recorded = r.matches - r.matchesWithoutRecord;
          return {
            ...r,
            key: `${r.matchType}|${r.divisionTier}|${r.matchOutcome}`,
            mode: modeLabel(r.matchType),
            division: divisionLabel(r.divisionTier),
            recorded,
            dropRate: ratio(r.matchesWithDrops, recorded),
            cardsPerMatch: ratio(r.cards, recorded),
          };
        })
        .sort(
          (a, b) =>
            a.mode.localeCompare(b.mode) ||
            a.divisionTier - b.divisionTier ||
            OUTCOME_ORDER[a.matchOutcome] - OUTCOME_ORDER[b.matchOutcome]
        ),
    [rows, activeMode]
  );

  const totals = useMemo(() => {
    const t = visible.reduce(
      (acc, r) => ({
        matches: acc.matches + r.matches,
        withDrops: acc.withDrops + r.matchesWithDrops,
        cards: acc.cards + r.cards,
        noRecord: acc.noRecord + r.matchesWithoutRecord,
      }),
      { matches: 0, withDrops: 0, cards: 0, noRecord: 0 }
    );
    const recorded = t.matches - t.noRecord;
    return {
      ...t,
      recorded,
      dropRate: ratio(t.withDrops, recorded),
      cardsPerMatch: ratio(t.cards, recorded),
    };
  }, [visible]);

  return (
    <Card
      loading={loading}
      title={
        <span>
          Card drops{' '}
          <Tooltip title="Match drops only, read from each completed match's recorded result — level-up, season pass and event grants are not included. Matches without a recorded result (older than completion caching) have unknown drops, not zero, and are left out of the rates. Unique players is distinct within a row and is not summed. Division is the player's current division, not the one held at match time.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </span>
      }
      extra={
        rows.length > 0 ? (
          <Select
            size="small"
            value={activeMode}
            onChange={setMode}
            options={modeOptions}
            style={{ width: 160 }}
          />
        ) : null
      }
    >
      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />}

      {rows.length === 0 ? (
        !error && (
          <Text type="secondary">
            No completed matches in this range. Drop counts come from the result each match records
            when it completes; matches from before that result was stored have no count.
          </Text>
        )
      ) : (
        <>
          <Row gutter={[16, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} sm={8}>
              <Statistic title="Cards dropped" value={fmt(totals.cards)} />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic title="Matches with drops" value={fmtPct(totals.dropRate)} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {fmt(totals.withDrops)} of {fmt(totals.recorded)} completed matches
                {totals.noRecord > 0 ? ' with a record' : ''}
              </Text>
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Cards per completed match"
                value={fmtPerMatch(totals.cardsPerMatch)}
              />
            </Col>
          </Row>

          {totals.noRecord > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Tag color="warning">
                {fmt(totals.noRecord)} {totals.noRecord === 1 ? 'match' : 'matches'} without a
                record
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Their drops are unknown, so they are left out of the rates and cards per match.
              </Text>
            </div>
          )}

          <Table<DropRow>
            rowKey="key"
            size="small"
            dataSource={visible}
            columns={COLUMNS}
            pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
          />
        </>
      )}
    </Card>
  );
};
